import { useLayoutEffect, useRef, type RefObject } from 'react';
import { DURATION, EASE } from '@constants/motion';
import { prefersReducedMotion } from '@lib/utils/motion';

type Box = { x: number; y: number; w: number; h: number };
/** Which way the indicator travels: a tab bar slides along x, the rail's bar along y, a pill in a grid both. */
export type SlideAxis = 'x' | 'y' | 'both';

const same = (a: Box, b: Box) => a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;

function place(group: HTMLElement, ind: HTMLElement, selector: string, axis: SlideAxis, last: { current: Box | null }, animate: boolean) {
  const active = group.querySelector<HTMLElement>(selector);
  if (!active) {
    ind.dataset.state = 'none';
    last.current = null;
    return;
  }
  const g = group.getBoundingClientRect();
  const a = active.getBoundingClientRect();
  // rects are in screen px; the indicator is placed in the group's own px, which differ under CSS zoom (monitor
  // mode) or a running transform: convert by the group's rendered-to-layout ratio
  const k = group.offsetWidth ? g.width / group.offsetWidth : 1;
  const box: Box = {
    x: axis === 'y' ? 0 : (a.left - g.left) / k - group.clientLeft + group.scrollLeft,
    y: axis === 'x' ? 0 : (a.top - g.top) / k - group.clientTop + group.scrollTop,
    w: a.width / k,
    h: a.height / k,
  };
  const prev = last.current;
  last.current = box;
  ind.dataset.state = 'on';
  if (prev && same(prev, box)) return;
  ind.style.setProperty('--ix', `${box.x}px`);
  ind.style.setProperty('--iy', `${box.y}px`);
  ind.style.setProperty('--iw', `${box.w}px`);
  ind.style.setProperty('--ih', `${box.h}px`);
  if (!animate || !prev || !box.w || !box.h || prefersReducedMotion()) return;
  // FLIP: start where the previous item was (and as wide or tall), end at rest; transform only
  const sx = axis === 'y' ? 1 : prev.w / box.w;
  const sy = axis === 'x' ? 1 : prev.h / box.h;
  ind.animate([{ transform: `translate(${prev.x}px, ${prev.y}px) scale(${sx}, ${sy})` }, { transform: `translate(${box.x}px, ${box.y}px)` }], { duration: DURATION.slow, easing: EASE.inOut });
}

/**
 * One indicator (a tab's underline, the selected pill of a switch, the rail's active bar) that slides to the
 * active item of `group` instead of each item drawing its own. The hook writes the active item's box as
 * --ix/--iy/--iw/--ih on the indicator (CSS: `transform: translate(var(--ix), var(--iy))`, transform-origin
 * 0 0) and `data-state="none"` when nothing is active. It slides when `active` changes; size changes (resize,
 * fonts, a badge appearing) and items added or removed later only move it.
 */
export function useSlidingIndicator<T extends HTMLElement = HTMLSpanElement>(group: RefObject<HTMLElement | null>, selector: string, active: unknown, axis: SlideAxis = 'x') {
  const indicator = useRef<T>(null);
  const last = useRef<Box | null>(null);
  useLayoutEffect(() => {
    if (group.current && indicator.current) place(group.current, indicator.current, selector, axis, last, true);
  }, [active, group, selector, axis]);
  useLayoutEffect(() => {
    const g = group.current;
    if (!g || typeof ResizeObserver === 'undefined') return;
    const again = () => indicator.current && place(g, indicator.current, selector, axis, last, false);
    const ro = new ResizeObserver(again);
    const watch = () => {
      ro.disconnect();
      ro.observe(g);
      for (const c of Array.from(g.children)) ro.observe(c);
    };
    watch();
    // items added or removed after mount (a tab that appears, a nav group for another role): follow them too
    const mo = new MutationObserver(() => {
      watch();
      again();
    });
    mo.observe(g, { childList: true });
    void document.fonts?.ready.then(again);
    return () => {
      ro.disconnect();
      mo.disconnect();
    };
  }, [group, selector, axis]);
  return indicator;
}
